import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  QaiInnerWrapComponent,
  QaiLabelComponent,
  QaiTooltipComponent,
  QaiIconComponent,
  QaiFloatingDirective,
  QaiToggleSwitchComponent,
  SortDirection,
  HeaderConfig,
} from '@qai/ui';
import { TranslationService } from 'src/app/services/translation.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { LeaderboardAggregate } from 'src/app/interfaces/leaderboard/ileaderboard-aggregate';
import { LeaderboardAggregatesResponse } from 'src/app/interfaces/leaderboard/response/ileaderboard-aggregate';
import { ISimulation } from 'src/app/interfaces/leaderboard/isimulation';
import { IAssignment } from 'src/app/interfaces/leaderboard/iassignment';
import { LeaderboardService } from 'src/app/services/leaderboard/leaderboard.service';
import { formatLongMonthDateTime } from 'src/app/utils/date-parser.util';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';

@Component({
  selector: 'app-leaderboard-content',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiTableComponent,
    QaiLoadingComponent,
    QaiInnerWrapComponent,
    QaiLabelComponent,
    QaiTooltipComponent,
    QaiIconComponent,
    QaiFloatingDirective,
    QaiToggleSwitchComponent,
  ],
  templateUrl: './leaderboard-content.component.html',
  styleUrls: ['./leaderboard-content.component.scss'],
})
export class LeaderboardContentComponent implements OnInit, OnChanges {
  @Input() selectedAssignment: IAssignment | null = null;
  @Input() selectedSimulation: ISimulation | null = null;

  tableData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  isLoading = true;
  hasData = false;
  isTranslationLoaded = false;
  totalEngagedUsersCount = 0;

  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'desc';
  sortingPriority: string[] = [];

  // Filter flags
  displayLowSkillsFilter: boolean = false;
  excludeLowSkills: boolean = false;

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private leaderboardService: LeaderboardService,
    private amplitudeService: AmplitudeService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.translationService.loadScopedTranslation('leaderboard').then(() => {
      this.isTranslationLoaded = true;
      this.translateHeaders();
      this.setInitialSortColumn();
    });

    // Get flag for skills filtering
    this.displayLowSkillsFilter = await this.featureFlagsService.isFlagEnabled(
      AppConstants.LEADERBOARD_SKILLS_FILTER_UI
    );
    console.log(
      `Flag for Leaderboard_Skills_Filter_UI: ${this.displayLowSkillsFilter}`
    );

    this.loadLeaderboardData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['selectedAssignment'] || changes['selectedSimulation']) &&
      this.isTranslationLoaded
    ) {
      this.totalEngagedUsersCount = 0;
      this.loadLeaderboardData();
    }
  }

  get headerText(): string {
    if (this.selectedSimulation === null || this.isLoading) {
      return '';
    }

    if (this.totalEngagedUsersCount === 0) {
      return this.translocoService.translate('leaderboard.NO_USERS_ENGAGED');
    }

   if (
      this.totalEngagedUsersCount <= 5 &&
      this.totalEngagedUsersCount > 0
    ) {
      return `${this.totalEngagedUsersCount}`;
    } else if (
      this.totalEngagedUsersCount <= 50 &&
      this.totalEngagedUsersCount > 5
    ) {
      return this.translocoService.translate('leaderboard.TOP5');
    } else {
      return this.translocoService.translate('leaderboard.TOP10PERCENT');
    }
  }

  get subtitleText(): string {
    if (this.totalEngagedUsersCount === 0) {
      return '';
    }

    if (this.totalEngagedUsersCount === 1) {
      return this.translocoService.translate('leaderboard.ENGAGED_USER');
    }

    if (this.totalEngagedUsersCount <= 5) {
      return this.translocoService.translate('leaderboard.ENGAGED_USERS');
    }

    const ofText = this.translocoService.translate('leaderboard.OF');
    const usersText = this.translocoService.translate(
      'leaderboard.ENGAGED_USERS'
    );
    return `${ofText} ${this.totalEngagedUsersCount} ${usersText}`;
  }

  private translateHeaders(): void {
    const highScoreName = this.translocoService.translate(
      'leaderboard.HIGH_SCORE'
    );
    const averageScoreName = this.translocoService.translate(
      'leaderboard.AVERAGE_SCORE'
    );
    const totalSessionsName = this.translocoService.translate(
      'leaderboard.TOTAL_SESSIONS'
    );
    const nameName = this.translocoService.translate('leaderboard.NAME');

    this.headers = [
      {
        name: nameName,
        sortable: false,
      },
      {
        name: highScoreName,
        sortable: true,
      },
      {
        name: averageScoreName,
        sortable: true,
      },
      {
        name: totalSessionsName,
        sortable: false,
      },
      {
        name: this.translocoService.translate('leaderboard.LAST_SESSION'),
        sortable: false,
      },
    ];

    this.sortingPriority = [averageScoreName, totalSessionsName, nameName];
  }

  private setInitialSortColumn(): void {
    this.initialSortColumn = this.translocoService.translate(
      'leaderboard.HIGH_SCORE'
    );
  }

  async loadLeaderboardData(): Promise<void> {
    if (!this.selectedAssignment || !this.selectedSimulation) {
      this.tableData = [];
      this.hasData = false;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    try {
      const accountUuid = localStorage.getItem('current_account_uuid');

      if (!accountUuid) {
        throw new Error('No account UUID found in localStorage');
      }

      const request = {
        accountUuid: accountUuid,
        moduleAssignmentUuid: this.selectedAssignment.moduleAssignmentUuid,
        callUuid: this.selectedSimulation.callUuid,
        excludeLowSkills: this.excludeLowSkills,
      };

      this.leaderboardService.findLeaderboardAggregates(request, withDemoModule(AppConstants.LEADERBOARD_UI)).subscribe({
        next: (response: LeaderboardAggregatesResponse) => {
          this.tableData = this.transformApiDataToTableRows(
            response.leaderboardAggregates
          );
          this.hasData = this.tableData.length > 0;
          this.totalEngagedUsersCount = response.totalEngagedUsersCount;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load leaderboard data:', error);
          this.tableData = [];
          this.hasData = false;
          this.totalEngagedUsersCount = 0;
          this.isLoading = false;
        },
      });
    } catch (error) {
      console.error('Error loading leaderboard data:', error);
      this.tableData = [];
      this.hasData = false;
      this.totalEngagedUsersCount = 0;
      this.isLoading = false;
    }
  }

  private transformApiDataToTableRows(
    apiData: LeaderboardAggregate[]
  ): TableRow[] {
    return apiData.map((entry) => this.createLeaderboardTableRow(entry));
  }

  private createLeaderboardTableRow(entry: LeaderboardAggregate): TableRow {
    const row = {
      id: entry.userName,
      [this.headers[0].name]: { type: 'text', value: entry.userName },
      [this.headers[1].name]: {
        type: 'text',
        value: entry.highScore?.toString() ?? '',
      },
      [this.headers[2].name]: {
        type: 'text',
        value: entry.averageScore?.toString() ?? '',
      },
      [this.headers[3].name]: {
        type: 'text',
        value: entry.totalSessions?.toString() ?? '',
      },
      [this.headers[4].name]: {
        type: 'text',
        value: formatLongMonthDateTime(entry.lastPractice),
      },
    } as const;
    return { ...row } as unknown as TableRow;
  }

  onSortChanged(event: any): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.LEADERBOARD_CLICK_HEADER,
      {
        header_name: event.column,
      }
    );
  }

  onSkillsFilteringChange(event: any) {
    this.excludeLowSkills = event.target.checked;
    console.log(`Skills filtering ${this.excludeLowSkills ? 'on' : 'off'}`);
    this.loadLeaderboardData();
  }
}
